
import { useState } from "react";
import { toast } from "sonner";
import type { Activity } from "../ActivityTypes";
import { fetchSuggestedActivities } from "../services/activityService";
import { publishA2AMessage } from "../utils/a2aActivityMessaging";

interface UseActivityTabsAndRefreshProps {
  refetchTodayActivities: () => void;
}

export const useActivityTabsAndRefresh = ({ refetchTodayActivities }: UseActivityTabsAndRefreshProps) => {
  const [targetTab, setTargetTab] = useState<"today" | "tomorrow">("today");
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [tomorrowActivities, setTomorrowActivities] = useState<Activity[] | null>(null);
  const [loadingTomorrow, setLoadingTomorrow] = useState(false);

  const handlePlanForTomorrow = async (isRefresh = false) => {
    setLoadingTomorrow(true);
    if (isRefresh) {
      toast.info("Refreshing tomorrow's plan...");
      publishA2AMessage('tomorrow-planning', "Refreshing plan for tomorrow");
    } else {
      toast.info("Generating tomorrow's plan...");
      publishA2AMessage('tomorrow-planning', "Planning activities for tomorrow");
    }

    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    try {
      const fetchedTomorrowActivities = await fetchSuggestedActivities(tomorrowDate);
      setTomorrowActivities(fetchedTomorrowActivities);
      toast.success(`Plan ${isRefresh ? 'refreshed' : 'generated'} for tomorrow!`);
      if (!isRefresh) {
        setTargetTab("tomorrow");
      }
      publishA2AMessage(
        'tomorrow-planning-success',
        `Successfully planned ${fetchedTomorrowActivities.length} activities for tomorrow`,
        { activityCount: fetchedTomorrowActivities.length }
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to ${isRefresh ? 'refresh' : 'generate'} plan for tomorrow.`);
      publishA2AMessage(
        'tomorrow-planning-error',
        `Error planning for tomorrow: ${errorMessage}`,
        { error: errorMessage },
        'high'
      );
    } finally {
      setLoadingTomorrow(false);
      setLastRefresh(new Date()); // Update last refresh time for both today and tomorrow tab actions
    }
  };
  
  const handleRefresh = () => {
    if (targetTab === "today") {
      refetchTodayActivities();
      setLastRefresh(new Date());
      toast.info("Refreshing today's activities...");
      publishA2AMessage('data-refresh', "Manually refreshing today's activities");
    } else {
      handlePlanForTomorrow(true); // Pass true to indicate it's a refresh
    }
  };

  const handleTabChange = (tab: "today" | "tomorrow") => {
    setTargetTab(tab);
    publishA2AMessage(
      'ui-interaction',
      `Switched to ${tab} activities tab`,
      { tabSelected: tab },
      'low'
    );
  };

  return {
    targetTab,
    setTargetTab: handleTabChange,
    lastRefresh,
    setLastRefresh, // Also expose setLastRefresh for interval updates
    tomorrowActivities,
    setTomorrowActivities, // Expose for mutation updates
    loadingTomorrow,
    handleRefresh,
    handlePlanForTomorrow,
  };
};
